import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, Empty, Field, dialogCls, inputCls } from "@/components/admin/ui";

const BLANK = { name: "", email: "", phone: "", organization: "", notes: "" };

const ClientForm = ({ initial, onSaved, onDelete }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, email: f.email, phone: f.phone, organization: f.organization, notes: f.notes };
      const { data } = f.id ? await api.put(`/admin/clients/${f.id}`, body) : await api.post("/admin/clients", body);
      onSaved(data);
      toast.success("Client saved");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="client-form">
      <Field label="Name"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="client-name-input" /></Field>
      <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="client-email-input" /></Field>
      <Field label="Phone"><input value={f.phone} onChange={set("phone")} className={inputCls} data-testid="client-phone-input" /></Field>
      <Field label="Church / organization"><input value={f.organization} onChange={set("organization")} className={inputCls} data-testid="client-org-input" /></Field>
      <Field label="Notes" className="sm:col-span-2"><textarea rows={4} value={f.notes} onChange={set("notes")} className={inputCls} data-testid="client-notes-input" /></Field>
      <div className="flex items-center justify-between sm:col-span-2">
        {f.id ? <Btn variant="danger" onClick={() => onDelete(f.id)} data-testid="client-delete-btn"><Trash2 className="h-4 w-4" /> Delete</Btn> : <span />}
        <Btn variant="primary" type="submit" disabled={busy} data-testid="client-save-btn">Save client</Btn>
      </div>
    </form>
  );
};

export const ClientsPanel = ({ clients, setClients, onNewContract, onNewInvoice }) => {
  const [editing, setEditing] = useState(null);

  const onSaved = (c) => {
    setClients((xs) => (xs.some((x) => x.id === c.id) ? xs.map((x) => (x.id === c.id ? c : x)) : [...xs, c].sort((a, b) => a.name.localeCompare(b.name))));
    setEditing(null);
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this client? Their unsigned contracts and unpaid invoices will be deleted too.")) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      setClients((xs) => xs.filter((x) => x.id !== id));
      setEditing(null);
      toast.success("Client deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="clients-panel">
      <div className="mb-4 flex justify-end">
        <Btn variant="primary" onClick={() => setEditing(BLANK)} data-testid="client-new-btn"><Plus className="h-4 w-4" /> New client</Btn>
      </div>
      {!clients.length ? <Empty testId="clients-empty">No clients yet. Add one, or turn an inquiry into a client.</Empty> : (
        <div className="divide-y divide-[#2E2A24] overflow-hidden rounded-2xl border border-[#2E2A24]">
          {clients.map((c) => (
            <div key={c.id} className="flex flex-col gap-3 bg-[#121215] px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`client-row-${c.id}`}>
              <button onClick={() => setEditing(c)} className="text-left">
                <span className="block font-medium text-[#F5EFE6]">{c.name}</span>
                <span className="block text-xs text-zinc-400">{[c.organization, c.email, c.phone].filter(Boolean).join(" · ")}</span>
              </button>
              <div className="flex gap-2">
                <Btn onClick={() => onNewContract(c.id)} data-testid={`client-new-contract-${c.id}`}>New contract</Btn>
                <Btn onClick={() => onNewInvoice(c.id)} data-testid={`client-new-invoice-${c.id}`}>New invoice</Btn>
                <Btn variant="danger" onClick={() => onDelete(c.id)} aria-label="Delete client" data-testid={`client-row-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing?.id ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
          {editing && <ClientForm key={editing.id || "new"} initial={{ ...BLANK, ...editing }} onSaved={onSaved} onDelete={onDelete} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
